import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Product from '../Product';
import { ProductGroup, NeuroMessage, usePages, buildPageUrl, useAnswers, notifyError, useNeuroState } from '@ignitesales/neuro-application/src/neuro-guide';
import { useUser } from '@ignitesales/neuro-application/src/neuro-guide/hooks';
import SendToInsightComplete from '../modals/SendToInsightComplete';
import { AnswersList } from '@ignitesales/neuro-application/src/neuro-guide/guide-framework';
import { startGuide, useDispatch } from '@ignitesales/neuro-application';
import { CompanyInfo, LookupFlow, VIEWS } from '../index.mjs';
import { sendToInsight, loadSubmittedProducts } from '../../actions/commerce-business-branch-actions.mjs';
import { createPortal } from 'react-dom';
import getAccountInfo from '../../services/Salesforce/getAccountInfo';
import ProductModal from '../modals/ProductModal';
import ReactSVG from 'react-svg';
import useLookupInfo from '../../hooks/useLookupInfo';
import useSendToInsight from '../../hooks/useSendToInsight';
import useSubmittedProducts from '../../hooks/useSubmittedProducts';
import useActionOnSubmit from '../../hooks/useActionOnSubmit';

function SummaryPage({ componentClass, productGroups }) {
  const { goToPreviousPage, sortPaths, paths } = usePages();
  const { uuid, getEmployee } = useUser();
  const { answers } = useAnswers();
  const [showSendToInsightComplete, setShowSendToInsightComplete] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [initialActiveTab, setInitialActiveTab] = useState('features');
  const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { lookupInfo, setLookupInfo, loadingCompanyInfo } = useLookupInfo();
  const { hasSentToInsight, onSendToInsightClicked } = useSendToInsight(lookupInfo, setLoading);
  const dispatch = useDispatch();
  
  const { submittedProductsState = {} } = useNeuroState();
  const submittedIds = submittedProductsState?.submittedIds || []; // Already submitted products ids, If found, allows to disable the 'Interested now'/addToCart btns.
  const { productWasSubmitted, saveSubmittedProducts } = useSubmittedProducts();
  const { openCreditCardLink } = useActionOnSubmit();

  const localAnswersKey = `${uuid}-localAnswers`;

  useEffect(() => {
    sortPaths();
    dispatch(loadSubmittedProducts());
  }, []);

  useEffect(() => { 
    // Only trigger, If sendToInsight360 was successful
    if(!!hasSentToInsight) {
      saveSubmittedProducts();
    }
  }, [hasSentToInsight]);

  const renderProductGroup = (productGroup, subGroups, products) => {
    const cssBEM = 'product-group';

    return products
  };

  const onBackToStartClick = () => {
    dispatch(startGuide('all'));
  }

  const handleInsightClick = () => {
    onSendToInsightClicked();
    openCreditCardLink();
  }

  const renderProduct = (product) => {
    return <Product
            product={product}
            key={product.id}
            summary
            isExisting={answers?.existingEnactCustomerProducts?.value.includes(`${product.id}`)}
            setShowProductModal={setShowProductModal}
            setSelectedProduct={setSelectedProduct}
            isSubmittedProduct={productWasSubmitted(product, submittedIds)}
            setInitialActiveTab={setInitialActiveTab}
          />;
  };

  const sendToInsightComplete = (
    <SendToInsightComplete
      className={`${componentClass}__insightModal insightComplete`}
      closeBtn={<ReactSVG src="/classpath/assets/images/icons/close.svg" />}
      isOpen={showSendToInsightComplete}
      onClose={() => setShowSendToInsightComplete(false)}
    />
  );

  const handleCompanyInfoEdit = value => {
    const { company } = lookupInfo;
    const localAnswers = JSON.parse(window.sessionStorage.getItem(localAnswersKey) || '{}');
    const key = company ? 'companyLookup' : 'contactLookup';
    const newAnswer = {
      appkey: key,
      timestamp: new Date(),
      value: {
        ...value,
        ...(company && { accountId: localAnswers[key]?.value?.accountId }),
        contactId: localAnswers[key]?.value?.contactId,
        isEdit: true,
      },
    };
    window.sessionStorage.setItem(
      localAnswersKey,
      JSON.stringify({
        ...localAnswers,
        [key]: newAnswer,
      })
    );
    setLookupInfo(company ? { company: newAnswer.value } : { contact: newAnswer.value });
  }

  const lookupModal = () =>
  (
    <LookupFlow
      isOpen={showCompanyInfoModal}
      onClose={() => setShowCompanyInfoModal(false)}
      value={lookupInfo.company || lookupInfo.contact}
      valueChanged={handleCompanyInfoEdit}
      type={VIEWS.EDIT_COMPANY}
    />
  );

  const productModal =
    (
      <ProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        closeBtn={<ReactSVG src="/classpath/assets/images/icons/close.svg" />}
        product={selectedProduct}
        isSubmittedProduct={productWasSubmitted(selectedProduct, submittedIds)}        initialActiveTab={initialActiveTab}
      />
    )

  const myRef = useRef(null);
  const scrollToAnswers = () => myRef.current.scrollIntoView({
    behavior: "smooth",
  });

  const renderCompanyInfo = () => (
    <CompanyInfo 
      data={lookupInfo.company || lookupInfo.contact} 
      onClick={() => setShowCompanyInfoModal(true)}
      isCompany={!!lookupInfo.company}
      isContact={!!lookupInfo.contact}
      isLoading={loadingCompanyInfo}
    />
  );

  const renderSpinner = () => <div className="summary-loading"><i className="fa fa-spinner fa-pulse fa-5x"></i></div>;
  return (
    <div className={componentClass}>
      {loading && createPortal(renderSpinner(), document.getElementById('root'))}
      <img src="/classpath/assets/images/circles_top-right.png" alt="Decorative circles at the top right" className="decorative-circles-top-right" />
      <div className={`${componentClass}__header`}>
        <div className={`${componentClass}__header--container`}>
          <div className={`${componentClass}__header--tag`}>
            <NeuroMessage id='summary.page.title' tagName='h1' />
          </div>
          {renderCompanyInfo()}

          </div>
        </div>
        <div className={`${componentClass}__header--answers`}>

        <nav className="summary-nav">
              <button className="btn" onClick={scrollToAnswers}>
                <ReactSVG src="/classpath/assets/images/icons/arrow_down.svg" />
                <NeuroMessage id='summary.answers.cta' tagName="div"/>
              </button>
              <button className="btn" onClick={onBackToStartClick} >
              <ReactSVG src="/classpath/assets/images/icons/restart_alt.svg" />
                <NeuroMessage id='summary.return.cta' tagName="div"/>
              </button>
            </nav>
      </div>

      <div className={`${componentClass}__products`}>{productGroups.map(g =>
        <ProductGroup group={g} renderGroupFn={renderProductGroup} renderProductFn={renderProduct} key={g.name} />
      )}
      </div>

      <div className="actions">
        <button onClick={goToPreviousPage} className="summaryPage__back mt-5"><NeuroMessage id="nav.prev" /></button>

        <button className="btn insight" onClick={handleInsightClick} disabled={hasSentToInsight}>
          <NeuroMessage id='summary.page.cta' />
        </button>
      </div>
      <div className={`send-status ${hasSentToInsight ? 'animated' : 'hidden'}`}>
        <i className="fa fa-check-circle checked" /><NeuroMessage id="summary.sendToInsight.status" />
      </div>

      <div className={`${componentClass}__answers`} ref={myRef}>
        <NeuroMessage id="summary.answers.title" tagName='h2' />
        <AnswersList linkToQuestion usePathOrder />
      </div>

      {lookupModal()}
      {sendToInsightComplete}
      {productModal}
      <img src="/classpath/assets/images/circles_bottom-left.png" alt="Decorative circles at the bottom left" className="decorative-circles-bottom-left" />
      <img src="/classpath/assets/images/circles_bottom-right.png" alt="Decorative circles at the bottom right" className="decorative-circles-bottom-right" />

    </div>
  );
}

SummaryPage.propTypes = {
  componentClass: PropTypes.string,
  productGroups: PropTypes.array,
};

export default SummaryPage;
